import React from 'react';
import { getHoneycombPattern } from './honeycombPatterns';

// Constants for hexagon dimensions
const CONSTANTS = {
  hexWidth: 19,       // Width of hexagon (rem)
  hexHeight: 17.8,    // Height of hexagon (rem)
  gapSize: -2,        // Gap between hexagons (rem) - negative to create honeycomb effect
  smallScreenBreakpoint: 768,  // Small screen breakpoint (px)
};

interface LayoutConfig {
  colSpacing: number;
  rowSpacing: number;
  offsetX: number;
  isSmallScreen: boolean;
}

/**
 * Calculate positions for a honeycomb layout
 */
export const calculateHoneycombPositions = (
  count: number, 
  windowWidth: number, 
  windowHeight: number
): React.CSSProperties[] => {
  if (count === 0) return [];
  
  const positions: React.CSSProperties[] = [];
  const isSmallScreen = windowWidth < CONSTANTS.smallScreenBreakpoint;
  
  // Get the basic layout configuration
  const layoutConfig = getLayoutConfig(count, isSmallScreen);
  
  // Get the honeycomb pattern based on count and screen size
  const honeycombPositions = getHoneycombPattern({ 
    isSmallScreen, 
    itemCount: count 
  });
  
  // Convert pattern positions to actual CSS positions
  for (let i = 0; i < count; i++) {
    const position = calculateItemPosition(
      honeycombPositions[i],
      layoutConfig,
      isSmallScreen,
      count,
      windowHeight,
      windowWidth
    );
    
    positions.push({
      position: 'absolute',
      transform: `translate(${position.x}rem, ${position.y}rem)`,
      transition: 'transform 0.3s ease',
      zIndex: i
    });
  }
  
  return positions;
};

/**
 * Get layout configuration based on item count and screen size
 */
const getLayoutConfig = (count: number, isSmallScreen: boolean): LayoutConfig => {
  // Calculate column and row spacing
  const colSpacing = CONSTANTS.hexWidth * 0.9 + CONSTANTS.gapSize;
  const rowSpacing = CONSTANTS.hexHeight * 0.9 + CONSTANTS.gapSize;
  
  // Determine column count for offset calculation
  const columnCount = getColumnCount(count, isSmallScreen);
  const offsetX = -((columnCount - 1) * colSpacing / 2);
  
  return {
    colSpacing,
    rowSpacing,
    offsetX,
    isSmallScreen
  };
};

/**
 * Determine column count based on item count and screen size
 */
const getColumnCount = (count: number, isSmallScreen: boolean): number => {
  if (isSmallScreen) return 2; // Always 2 columns on small screens
  if (count <= 3) return 2;
  if (count <= 9) return 3;
  return Math.ceil(count / 2); // Fallback
};

/**
 * Calculate the position for a single item
 */
const calculateItemPosition = (
  gridPosition: [number, number],
  layoutConfig: LayoutConfig,
  isSmallScreen: boolean,
  itemCount: number,
  windowHeight: number,
  windowWidth: number
) => {
  const [col, row] = gridPosition;
  const { colSpacing, rowSpacing, offsetX } = layoutConfig;
  
  if (isSmallScreen) {
    return calculateSmallScreenPosition(col, row, itemCount, windowHeight, windowWidth);
  } else {
    // Normal positioning for larger screens
    return {
      x: col * colSpacing + offsetX,
      y: row * rowSpacing
    };
  }
};

/**
 * Calculate position for small screen layout
 */
const calculateSmallScreenPosition = (
  col: number, 
  row: number, 
  itemCount: number, 
  windowHeight: number,
  windowWidth: number
) => {
  // For small screens with honeycomb pattern
  // Adjust spacing for small screens
  const smallColSpacing = CONSTANTS.hexWidth * 0.6;
  const smallRowSpacing = CONSTANTS.hexHeight * 0.6;
  
  // Convert viewport dimensions to rem (assuming 1rem = 16px)
  const viewportHeightInRem = windowHeight / 16;
  const viewportWidthInRem = windowWidth / 16;
  
  // Calculate the total height of the pattern for vertical centering
  // For small screens with 2 columns (left and right), calculate height based on the honeycomb pattern
  // Where each column's height is calculated as: 1 + 0.5 * (itemsInColumn - 1)
  
  // Determine number of items in each column
  const leftColumnItems = Math.ceil(itemCount / 2);
  const rightColumnItems = Math.floor(itemCount / 2);
  
  // Calculate the height in "rows" for each column
  // For a honeycomb pattern, each additional item adds 0.5 rows due to the offset
  const leftColumnHeight = leftColumnItems > 0 ? 1 + 0.5 * (leftColumnItems - 1) : 0;
  const rightColumnHeight = rightColumnItems > 0 ? 1 + 0.5 * (rightColumnItems - 1) : 0;
  
  // Use the taller column for the pattern height
  const tallestColumnHeight = Math.max(leftColumnHeight, rightColumnHeight);
  
  // Calculate actual pattern height in rem
  var patternHeight = tallestColumnHeight * smallRowSpacing;
  
  // Add one more hexagon height to account for the full height
  patternHeight += CONSTANTS.hexHeight;
  
  // Calculate vertical center offset
  // Take half of viewport height, then subtract half of pattern height
  const verticalCenterOffset = (viewportHeightInRem / 2) - (patternHeight / 2);
  
  // Calculate aspect ratio (width / height)
  const aspectRatio = windowWidth / windowHeight;
  
  // Calculate responsive adjustment for vertical position
  // let responsiveAdjustment = calculateResponsiveAdjustment(aspectRatio, viewportHeightInRem, itemCount);
  let responsiveAdjustment = -viewportHeightInRem * itemCount * 0.085;
  
  // Calculate position
  const xPos = col * smallColSpacing;
  const yPos = row * smallRowSpacing + verticalCenterOffset + responsiveAdjustment;
  
  // Apply horizontal offset to center the pattern
  // Use viewport width to ensure proper centering
  let smallScreenOffsetX = viewportWidthInRem / 2 - smallColSpacing;
  
  return {
    x: xPos + smallScreenOffsetX,
    y: yPos
  };
};

/**
 * Calculate vertical adjustment based on aspect ratio and item count
 */
const calculateResponsiveAdjustment = (
  aspectRatio: number, 
  viewportHeightInRem: number,
  itemCount: number
): number => {
  // Base adjustment value
  let responsiveAdjustment = 0;
  
  if (itemCount <= 6) {
    // For 3-6 items, maintain current vertical position
    if (aspectRatio < 0.65) {
      // Portrait orientation
      responsiveAdjustment = -viewportHeightInRem * 0.25;
    } else {
      // Landscape orientation
      responsiveAdjustment = -viewportHeightInRem * 0.20;
    }
  } else {
    // For 7+ items, adjust to prevent downward shift
    if (aspectRatio < 0.65) {
      // Portrait orientation - move up for more items
      responsiveAdjustment = -viewportHeightInRem * 0.35;
    } else {
      // Landscape orientation
      responsiveAdjustment = -viewportHeightInRem * 0.30;
    }
  }
  
  // Scale adjustment based on item count
  const countScaleFactor = Math.min(1, 6 / itemCount); // Reduces adjustment for larger counts
  responsiveAdjustment *= countScaleFactor;
  
  // Apply minimum and maximum bounds to the adjustment
  const minAdjustment = -25; // Don't go lower than -25rem
  const maxAdjustment = -5;  // Don't go higher than -5rem
  
  return Math.max(Math.min(responsiveAdjustment, maxAdjustment), minAdjustment);
};